import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './PlatformPage.css';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

/* ================================================================
   ICONS (inline SVG, consistent with the rest of the site)
   ================================================================ */
const Icon = {
  reliability: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  scale: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a13 13 0 0 1 0 18" />
      <path d="M12 3a13 13 0 0 0 0 18" />
    </svg>
  ),
  performance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  ),
  efficiency: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2a7 7 0 0 0-7 7c0 3 2 5 3 6.5V19a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-3.5C17 14 19 12 19 9a7 7 0 0 0-7-7z" />
      <path d="M9 22h6" />
    </svg>
  ),
  security: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      <circle cx="12" cy="15.5" r="1" />
    </svg>
  ),
  compliance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  cloud: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.5 19a4.5 4.5 0 1 0-1.6-8.7A6 6 0 0 0 4 11.5 4.5 4.5 0 0 0 6.5 19h11z" />
    </svg>
  ),
  vpc: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
      <circle cx="7" cy="6.5" r="0.6" fill="currentColor" />
      <circle cx="9.5" cy="6.5" r="0.6" fill="currentColor" />
    </svg>
  ),
  hybrid: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="12" r="4" />
      <circle cx="16" cy="12" r="4" />
    </svg>
  ),
  onprem: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="6" rx="1" />
      <rect x="3" y="14" width="18" height="6" rx="1" />
      <circle cx="7" cy="7" r="0.7" fill="currentColor" />
      <circle cx="7" cy="17" r="0.7" fill="currentColor" />
    </svg>
  )
};

/* ================================================================
   DATA
   ================================================================ */
const heroStats = [
  { value: '99.999%', label: 'Multi-cloud uptime' },
  { value: '\u00b5s\u2013ms', label: 'Reasoning latency' },
  { value: '10\u2013100x', label: 'Lower power vs LLM stacks' },
  { value: 'HIPAA / SOC2 / GDPR', label: 'Compliance posture', wide: true },
  { value: 'Any cloud / On-prem / Air-gapped', label: 'Deployment surfaces', wide: true }
];

const pillars = [
  {
    id: 'reliability',
    icon: Icon.reliability,
    name: 'Reliability',
    promise: 'Five-nines uptime, no matter what fails.',
    chipGroups: [
      {
        items: [
          'Active-active multi-cloud',
          'Automatic regional failover',
          'Zone-redundant compute',
          'Distributed semantic graph sync',
          'Intelligent geo-routing'
        ],
        variant: 'pill'
      }
    ],
    tagline: 'The system does not fail \u2014 it heals, reroutes, and continues.'
  },
  {
    id: 'scale',
    icon: Icon.scale,
    name: 'Scale',
    promise: 'Built for the largest enterprises in the world.',
    chipGroups: [
      {
        items: [
          'Billions of reasoning events',
          'Petabyte semantic graphs',
          'Millions of decision flows / day',
          'Globally distributed streams',
          'Multi-region agents & apps',
          'Real-time cross-domain fusion'
        ],
        variant: 'pill'
      }
    ],
    tagline: 'Same architecture from pilot to Fortune-50 scale \u2014 no redesign required.'
  },
  {
    id: 'performance',
    icon: Icon.performance,
    name: 'Performance',
    promise: 'Microsecond-to-millisecond reasoning, even on deep multi-step chains.',
    chipGroups: [
      {
        items: [
          'No autoregressive decoding',
          'Real-time clinical guidance',
          'Real-time claims adjudication',
          'Real-time compliance checks',
          'Real-time fraud detection',
          'Real-time agent orchestration'
        ],
        variant: 'pill'
      }
    ],
    tagline: 'Speed LLM-driven systems simply cannot match.'
  },
  {
    id: 'efficiency',
    icon: Icon.efficiency,
    name: 'Efficiency',
    promise: 'GPU-optional. CPU-friendly. Sustainable by design.',
    chipGroups: [
      {
        items: [
          'No GPUs needed for reasoning',
          'CPU-only workloads',
          'GPUs only for language tasks',
          '10\u2013100x lower power',
          'SCO2-compatible footprint'
        ],
        variant: 'pill'
      }
    ],
    tagline: 'Enterprise-grade AI, finally affordable to run at production volume.'
  },
  {
    id: 'security',
    icon: Icon.security,
    name: 'Security & Trust',
    promise: 'A reasoning engine with a verifiable chain of logic.',
    chipGroups: [
      {
        items: [
          'Zero Trust architecture',
          'Enterprise IAM & RBAC',
          'Field-level access control',
          'Data residency controls',
          'In-flight + at-rest encryption',
          'Full audit & trace logs',
          'Verifiable chain of logic'
        ],
        variant: 'pill'
      }
    ],
    tagline: 'Trust is engineered, not optional.'
  },
  {
    id: 'compliance',
    icon: Icon.compliance,
    name: 'Compliance',
    promise: 'Made for the most regulated industries in the world.',
    chipGroups: [
      {
        items: [
          'HIPAA',
          'SOC2 / SOC2+',
          'GDPR',
          'Data residency + erasure',
          'Enforceable decision chains',
          'EU AI Act-ready'
        ],
        variant: 'pill'
      }
    ],
    tagline: 'Deployable in the most sensitive operational environments anywhere.'
  }
];

const deployments = [
  {
    id: 'saas',
    icon: Icon.cloud,
    tier: 'Shared SaaS Cloud',
    bestFor: 'Fastest onboarding, lowest cost',
    features: [
      'Auto-scaling',
      'Continuous platform updates',
      'Ideal for non-regulated workflows'
    ]
  },
  {
    id: 'dedicated',
    icon: Icon.vpc,
    tier: 'Dedicated Single-Tenant Cloud',
    bestFor: 'Mid-to-large enterprises',
    features: [
      'Full tenant isolation',
      'Dedicated VPC',
      'Custom compliance controls'
    ]
  },
  {
    id: 'hybrid',
    icon: Icon.hybrid,
    tier: 'Private Cloud / Hybrid',
    bestFor: 'Healthcare, finance, defense, public sector',
    features: [
      'Runs in your AWS / Azure / GCP / OCI',
      'On-prem Kubernetes supported',
      'Sovereign + air-gapped deployments'
    ]
  },
  {
    id: 'onprem',
    icon: Icon.onprem,
    tier: 'On-Premise / Bare-Metal',
    bestFor: 'Highest-sensitivity workloads',
    features: [
      'No external connectivity required',
      'Full control of model and data',
      'Engineered for sealed environments'
    ]
  }
];

const rescueBefore = [
  'Your LLMs',
  'Your RAG stack',
  'Your embeddings',
  'Your data warehouse',
  'Your rule systems',
  'Your APIs',
  'Your workflows',
  'Your analytics',
  'Your custom apps'
];

const rescueAfter = [
  'Hallucinations stop',
  'Policy enforced',
  'Perfect context, every call',
  'LLMs governed, not trusted blindly',
  'Explainability added',
  'Cross-domain reasoning',
  'Deterministic decisioning',
  'No more retraining cycles',
  'Automation previously impossible \u2014 unlocked'
];

const autonomyUses = [
  'Fully autonomous claims processing',
  'Fully autonomous clinical decision support',
  'Fully autonomous risk & compliance checks',
  'Fully autonomous financial controls',
  'Fully autonomous customer operations',
  'Fully autonomous supply chain intelligence'
];

const closingPromises = [
  'Reliable',
  'Scalable',
  'Fast',
  'Efficient',
  'Secure',
  'Compliant'
];

/* ================================================================
   PRIMITIVES
   ================================================================ */
function ChipCloud({ label, items, variant }) {
  const cls =
    variant === 'pill' ? 'platform-pill-row'
    : variant === 'negative' ? 'platform-pill-row platform-pill-row--negative'
    : 'platform-chips';
  const itemCls =
    variant === 'pill' ? 'platform-pill'
    : variant === 'negative' ? 'platform-pill platform-pill--negative'
    : 'platform-chip';

  return (
    <div className="platform-chip-group">
      {label && <div className="platform-chip-group__label">{label}</div>}
      <div className={cls}>
        {items.map((item, i) => (
          <span key={i} className={itemCls}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function HeroStats({ stats }) {
  return (
    <div className="platform-stats">
      {stats.map((s, i) => (
        <motion.div
          key={i}
          className={`platform-stat ${s.wide ? 'platform-stat--wide' : ''}`}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.05 }}
        >
          <div className="platform-stat__value">{s.value}</div>
          <div className="platform-stat__label">{s.label}</div>
        </motion.div>
      ))}
    </div>
  );
}

function PillarCard({ pillar, index }) {
  return (
    <motion.article
      className="platform-pillar-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.05 }}
    >
      <div className="platform-pillar__icon">{pillar.icon}</div>
      <h3 className="platform-pillar__name">{pillar.name}</h3>
      <p className="platform-pillar__promise">{pillar.promise}</p>
      <div className="platform-pillar__chips">
        {pillar.chipGroups.map((g, i) => (
          <ChipCloud key={i} label={g.label} items={g.items} variant={g.variant} />
        ))}
      </div>
      {pillar.tagline && (
        <div className="platform-pillar__tagline">{pillar.tagline}</div>
      )}
    </motion.article>
  );
}

function DeploymentCard({ tier, index }) {
  return (
    <motion.article
      className="platform-deploy-card"
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
    >
      <div className="platform-deploy__icon">{tier.icon}</div>
      <div className="platform-deploy__tier">{tier.tier}</div>
      <div className="platform-deploy__bestfor-row">
        <span className="platform-deploy__bestfor-label">Best for</span>
        <span className="platform-deploy__bestfor">{tier.bestFor}</span>
      </div>
      <ul className="platform-deploy__features">
        {tier.features.map((f, i) => (
          <li key={i}>
            <span className="platform-deploy__check" aria-hidden="true">&#10003;</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

function RescueStack({ before, after }) {
  return (
    <div className="platform-rescue">
      <section className="platform-rescue__col platform-rescue__col--before">
        <div className="platform-rescue__col-tag">YOUR EXISTING STACK</div>
        <ul className="platform-rescue__list">
          {before.map((b, i) => (
            <li key={i}>
              <span className="platform-rescue__bullet platform-rescue__bullet--before" aria-hidden="true"></span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="platform-rescue__connector" aria-hidden="true">
        <div className="platform-rescue__connector-pill">
          <span className="platform-rescue__connector-label">Wrap with</span>
          <span className="platform-rescue__connector-name">CerebralMachines</span>
        </div>
        <div className="platform-rescue__arrow">&rarr;</div>
      </div>

      <section className="platform-rescue__col platform-rescue__col--after">
        <div className="platform-rescue__col-tag">WHAT IT BECOMES</div>
        <ul className="platform-rescue__list">
          {after.map((a, i) => (
            <li key={i}>
              <span className="platform-rescue__bullet platform-rescue__bullet--after" aria-hidden="true">&#10003;</span>
              <span>{a}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function AutonomyTile({ use, index }) {
  return (
    <motion.div
      className="platform-auto-tile"
      initial={{ opacity: 0, scale: 0.97 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: (index % 3) * 0.05 }}
    >
      <span className="platform-auto-tile__num">{String(index + 1).padStart(2, '0')}</span>
      <p className="platform-auto-tile__text">{use}</p>
    </motion.div>
  );
}

function ClosingSummary({ promises }) {
  return (
    <section className="platform-summary">
      <div className="platform-container">
        <motion.div {...fadeInUp} className="platform-summary__inner">
          <span className="platform-eyebrow">THE ENTERPRISE-GRADE COGNITIVE BACKBONE</span>
          <h2 className="platform-summary__title">One Platform. Every Promise.</h2>
          <p className="platform-summary__lede">
            One unified cognitive backbone for every AI surface in your enterprise &mdash;
            reliable, scalable, fast, efficient, secure, and compliant. Same behavior across every
            cloud, every region, and every deployment model.
          </p>
          <div className="platform-summary__promises">
            {promises.map((p, i) => (
              <span key={i} className="platform-summary__chip">{p}</span>
            ))}
          </div>
          <div className="platform-summary__arrow" aria-hidden="true">&darr;</div>
          <div className="platform-summary__platform">
            <span className="platform-summary__platform-label">Powered by</span>
            <span className="platform-summary__platform-name">Hybrid Cognitive Linguistic Core (HCLC)</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================
   PAGE
   ================================================================ */
function PlatformPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="platform-page">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="platform-hero">
        <div className="platform-container">
          <motion.div {...fadeInUp}>
            <span className="platform-hero-eyebrow">PLATFORM</span>
            <h1 className="platform-hero-title">
              Robust. Global. Enterprise-Grade.<br />
              Built for Level-5 AI Autonomy.
            </h1>
            <p className="platform-hero-lede">
              CerebralMachines is engineered for mission-critical enterprise operations &mdash; not lab demos.
              You get the reliability, security, and performance that healthcare, financial, regulatory,
              and global-scale organizations require, without the GPU-heavy infrastructure other AI systems depend on.
            </p>
            <div className="platform-hero-ctas">
              <button className="btn-primary-large" onClick={() => navigate('/request-demo')}>Request a Demo</button>
              <button className="btn-secondary-large" onClick={() => navigate('/use-cases')}>See it in action</button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero stats bar */}
      <section className="platform-stats-section">
        <div className="platform-container">
          <HeroStats stats={heroStats} />
        </div>
      </section>

      {/* Six Pillars */}
      <section className="platform-pillars-section">
        <div className="platform-container">
          <motion.div {...fadeInUp} className="platform-section-head">
            <span className="platform-eyebrow">SIX ENTERPRISE PILLARS</span>
            <h2 className="platform-h2">Engineered for Production, Not Demos</h2>
            <p className="platform-section-lede">
              Six promises the platform delivers on by design &mdash; not by adding more services on top.
            </p>
          </motion.div>
          <div className="platform-pillars">
            {pillars.map((p, i) => (
              <PillarCard key={p.id} pillar={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Deployment Matrix */}
      <section className="platform-deploy-section">
        <div className="platform-container">
          <motion.div {...fadeInUp} className="platform-section-head">
            <span className="platform-eyebrow">DEPLOYMENT MODELS</span>
            <h2 className="platform-h2">Deploy It Where Your Operations Live</h2>
            <p className="platform-section-lede">
              Pick the deployment posture that matches your operational, regulatory, and cost reality.
              The platform behaves identically across all four &mdash; fast, deterministic, globally resilient.
            </p>
          </motion.div>
          <div className="platform-deploy-matrix">
            {deployments.map((d, i) => (
              <DeploymentCard key={d.id} tier={d} index={i} />
            ))}
          </div>
          <p className="platform-deploy-footer">
            Same behavior across every tier. Same APIs. Same guarantees.
          </p>
        </div>
      </section>

      {/* Rescue Stack */}
      <section className="platform-rescue-section">
        <div className="platform-container">
          <motion.div {...fadeInUp} className="platform-section-head">
            <span className="platform-eyebrow">RESCUE YOUR STUCK AI</span>
            <h2 className="platform-h2">Wraps Your Existing Stack &mdash; Doesn&rsquo;t Replace It</h2>
            <p className="platform-section-lede">
              CerebralMachines absorbs the AI investments you have already made and stabilizes them.
              No rip-and-replace. No retraining cycles. No new vendor sprawl.
            </p>
          </motion.div>
          <RescueStack before={rescueBefore} after={rescueAfter} />
          <div className="platform-rescue-callout">
            Most enterprises don&rsquo;t need <em>more</em> AI. They need the missing semantic and cognitive
            layer that makes everything else work.
          </div>
        </div>
      </section>

      {/* Level-5 Autonomy */}
      <section className="platform-auto-section">
        <div className="platform-container">
          <motion.div {...fadeInUp} className="platform-section-head">
            <span className="platform-eyebrow">LEVEL-5 AUTONOMY</span>
            <h2 className="platform-h2 platform-h2--gradient">Real Autonomy. Not Assistants. Not Copilots. Not Prototypes.</h2>
            <p className="platform-section-lede">
              With CerebralMachines you finally have the infrastructure to build genuinely autonomous
              enterprise systems &mdash; governed, explainable, and trusted by auditors, regulators,
              and executives.
            </p>
          </motion.div>

          <div className="platform-auto__not">
            <span className="platform-auto__not-pill">Not assistants</span>
            <span className="platform-auto__not-pill">Not copilots</span>
            <span className="platform-auto__not-pill">Not prototypes</span>
          </div>

          <div className="platform-auto__grid">
            {autonomyUses.map((use, i) => (
              <AutonomyTile key={i} use={use} index={i} />
            ))}
          </div>

          <div className="platform-auto__final">
            CerebralMachines makes Level-5 AI autonomy realistic, safe, and deployable today.
          </div>
        </div>
      </section>

      {/* Closing Summary */}
      <ClosingSummary promises={closingPromises} />

      {/* CTA */}
      <section className="platform-cta">
        <div className="platform-container">
          <motion.div {...fadeInUp} className="platform-cta-content">
            <h2 className="platform-cta-title">Ready to Deploy Enterprise-Grade AI?</h2>
            <p className="platform-cta-text">
              See how CerebralMachines delivers 99.999% uptime, microsecond performance, and Level-5
              autonomy on the infrastructure your business already runs on.
            </p>
            <div className="platform-cta-buttons">
              <button className="btn-primary-large" onClick={() => navigate('/request-demo')}>Request a Demo</button>
              <button className="btn-secondary-large" onClick={() => navigate('/use-cases')}>See real journeys</button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">
                Building the cognitive backbone for enterprise AI.
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PlatformPage;
